import * as Sentry from "@sentry/react"
import { create } from "apisauce"
import { getFunctions, httpsCallable } from "firebase/functions"

import { EXPECTED_FUNCTION_ERROR_CODES } from "@/utils/sentryExpectedThrows"
import type { FleetServiceType } from "@/utils/serviceTypeAvailability"

import firebaseApp, { auth, waitForAuthReady } from "./auth"
import { runWithRetry, READ_DEFAULTS, MUTATION_DEFAULTS, RetryPolicy } from "./coldStartRetry"

// Type definitions for API endpoints
export interface TrackingCredentials {
  orderId?: string // Optional - can use token-only access
  trackingToken: string // This is the meeting_secret_meetergo from HubSpot
}

export interface PublicOrderTracking {
  id: string
  numberPlate: string // Masked: "B-XX 1234"
  customerName: string // Masked: "John D."
  status: string
  orderPhase: string
  serviceType: "pickup_delivery" | "driveby"

  // Driver information
  pickupDriverName?: string // Formatted as "FirstName L."
  returnDriverName?: string // Formatted as "FirstName L."

  // Hub location (pickup/return point)
  hubLocation?: {
    name: string
    address: string
    latitude: number
    longitude: number
  }

  // Current driver location (updated every 60s in Firestore)
  currentDriverLocation?: {
    latitude: number
    longitude: number
    timestamp: number
    isReturn: boolean
  }

  // PDF report availability
  hasPdfReport: boolean
  inspectionPassed?: boolean

  // Reschedule/cancel links from HubSpot
  ticketRescheduling?: string
  ticketCancellation?: string

  // Order source
  orderSource?:
    | "b2c-website"
    | "b2c-website-carassist-tracking-rebook"
    | "fleethub"
    | "orbitcontrol"

  // Metadata
  trackingValid: boolean
  lastLocationUpdate?: any // Firestore Timestamp
  updatedAt: any // Firestore Timestamp
}

export interface PusherAuthRequest {
  socketId: string
  channelName: string
  orderId: string
  trackingToken: string
}

export interface PusherAuthResponse {
  auth: string
  channel_data?: string
}

export interface PDFDownloadRequest {
  orderId: string
  trackingToken: string
}

export interface PDFDownloadResponse {
  url: string // Time-limited download URL (24 hours)
  expiresAt: number // Timestamp when URL expires
  filename: string // Original filename
  size?: number // File size in bytes (optional)
}

export interface HubLocation {
  id: string
  location: string
  address: string
  latitude: number
  longitude: number
  city: string
  street?: string
  number?: string
  postcode?: string
  openingHours?: {
    schedule: WeeklySchedule
    timezone: string
    specialDates?: { [date: string]: SpecialDate }
  }
}

export interface LocationsResponse {
  success: boolean
  locations: HubLocation[]
  error?: string
}

export interface CustomerLocationData {
  id: string // HubSpot company ID to identify the company
  latitude: number
  longitude: number
  companyName: string
  address: string
  isParent: boolean // Whether this is the parent company
}

export interface ChildCompany {
  companyName: string
  hubspotId: string
  isActive: boolean
  latitude?: number
  longitude?: number
  postcode?: string
  street?: string
  city?: string
  expansionRegion?: string
}

export interface CompanyOption {
  id: string
  name: string
  expansionRegion?: string
  estimatedLegMinutes?: number
}

export interface CustomerCompaniesResponse {
  parentCompany: CompanyOption
  childCompanies: CompanyOption[]
}

export interface FleetHubMapData {
  customerLocations: CustomerLocationData[]
  hubLocations: HubLocation[]
}

// Create API client for HTTP endpoints
const trackingApi = create({
  baseURL: "https://validatetrackingaccesshttp-u5rowvysnq-oe.a.run.app",
  headers: {
    "Content-Type": "application/json",
  },
})

// Create API client for referral survey HTTP endpoint
const surveyApi = create({
  baseURL: "https://submitreferralsurveyhttp-u5rowvysnq-oe.a.run.app",
  headers: {
    "Content-Type": "application/json",
  },
})

// Create API client for Firebase Functions HTTP endpoints
const firebaseFunctionsApi = create({
  baseURL: "https://europe-west10-liria-ai-inspect-backend.cloudfunctions.net",
  headers: {
    "Content-Type": "application/json",
  },
})

// Initialize Firebase Functions for callable functions (requires authentication)
const functions = getFunctions(firebaseApp, "europe-west10")

/**
 * Calls a Firebase HTTPS callable with an automatic token-refresh retry.
 *
 * Guards against two failure modes:
 * 1. Cold-load race — awaits auth.authStateReady() so the callable never fires
 *    before Firebase has rehydrated the persisted session (~50-500 ms on web).
 * 2. Stale cached ID token — on `functions/unauthenticated` with a signed-in user,
 *    forces a token refresh (getIdToken(true)) and retries exactly once.
 *
 * If the second attempt also fails, the error is re-thrown unchanged.
 * If auth.currentUser is null after ready the caller should have already redirected
 * to login; this will throw unauthenticated (which the store surfaces to the user).
 */
export async function callableWithAuthRetry<T, R>(
  name: string,
  payload: T,
  policy: RetryPolicy = "mutation",
): Promise<R> {
  await waitForAuthReady()

  const invoke = () => httpsCallable<T, R>(functions, name)(payload)
  const defaults = policy === "read" ? READ_DEFAULTS : MUTATION_DEFAULTS

  return runWithRetry(
    async () => {
      try {
        return (await invoke()).data
      } catch (e: any) {
        const isUnauthenticated =
          e?.code === "functions/unauthenticated" || e?.code === "unauthenticated"
        if (isUnauthenticated && auth.currentUser) {
          await auth.currentUser.getIdToken(true)
          return (await invoke()).data
        }
        throw e
      }
    },
    policy,
    defaults,
  )
}

/**
 * Rewraps a callable rejection as a display-ready Error **without losing the
 * original `FirebaseError.code`**.
 *
 * A bare `new Error(message)` drops `.code`, and that single omission breaks two
 * things downstream. `downgradeExpectedThrow` can no longer tier the event by
 * code and falls back to fragile message-substring matching — the exact failure
 * its own docstring predicts (`sentryExpectedThrows.ts:33-41`), which is why the
 * B2B guard denial reached Sentry at ERROR instead of WARN as CARASSIST-WEB-2T.
 * And callers cannot tell an access denial apart from any other failure, so the
 * session-recovery path in `b2bAccess.isB2bAccessDenied` has nothing to match on.
 *
 * The original error is kept as `cause` so its untranslated backend message
 * survives even when the display message replaces it. PLA-534.
 */
export function rewrapCallableError(message: string, original: unknown): Error {
  const wrapped = new Error(message) as Error & { code?: string; cause?: unknown }
  const code = (original as { code?: unknown } | null | undefined)?.code
  if (typeof code === "string") wrapped.code = code
  wrapped.cause = original
  return wrapped
}

/**
 * Returns true for network-layer transients (cellular drop, DNS hiccup, request
 * timeout). Safe to suppress across every flow. The retry loop handles these;
 * `sync-retry-exhausted` fires once retries fully exhaust (~4 min).
 *
 * Response-layer 404s are NOT included here: a "not found" can be a genuine
 * backend regression in flows like PDF download or survey submission, and
 * suppressing it kills observability. Only validateAndFetchOrder treats 404
 * as expected (HubSpot → Firestore sync window) — handled inline at its catch.
 */
function isTransientNetworkError(error: any): boolean {
  const msg: string = error?.message ?? ""
  return (
    msg.includes("NETWORK_ERROR") ||
    msg.includes("CONNECTION_ERROR") ||
    msg.includes("TIMEOUT_ERROR")
  )
}

// Helper functions
export async function validateAndFetchOrder(
  orderId: string | undefined,
  trackingToken: string,
): Promise<PublicOrderTracking> {
  try {
    // Use HTTP endpoint instead of callable function to avoid CORS issues
    const response = await trackingApi.post<PublicOrderTracking>("/", {
      orderId,
      trackingToken,
    })

    if (!response.ok) {
      // Handle error based on status code and error response
      const errorData = response.data as any
      const _errorCode = errorData?.code || ""

      switch (response.status) {
        case 401:
          throw new Error(errorData?.error || "Invalid or expired tracking link")
        case 404:
          throw new Error(errorData?.error || "Order not found")
        case 412:
          throw new Error(errorData?.error || "No tracking information available")
        case 400:
          throw new Error(errorData?.error || "Invalid request")
        default:
          throw new Error(errorData?.error || `Failed to validate access: ${response.problem}`)
      }
    }

    // Backend returns 200 + { code: "pending" } during the ~40s HubSpot → Firestore
    // sync window. Translate it into the same "Order not found" error so the store's
    // retry loop engages; Sentry alert fires only after retries exhaust (~4 min).
    const data = response.data as any
    if (data?.code === "pending") {
      throw new Error("Order not found")
    }

    return response.data as PublicOrderTracking
  } catch (error: any) {
    // 404/pending and transient network errors are expected during normal operation
    // (webhook sync window + flaky cellular). The retry loop self-heals; Sentry fires
    // only via `sync-retry-exhausted` after all 80 retries are exhausted (~4 min).
    const msg: string = error?.message ?? ""
    const isSyncWindowMiss = msg.includes("not found") || msg.includes("404")
    if (!isTransientNetworkError(error) && !isSyncWindowMiss) {
      Sentry.captureException(error, {
        tags: { flow: "tracking", operation: "validate-order" },
      })
    }
    // Re-throw if it's already a processed error
    if (error.message) {
      throw error
    }
    // Otherwise, throw a generic error
    throw new Error(`Failed to validate access: ${error}`)
  }
}

export async function getOrderByToken(trackingToken: string): Promise<PublicOrderTracking> {
  // Use validateTrackingAccess without orderId for token-only access
  return validateAndFetchOrder(undefined, trackingToken)
}

export async function downloadPDFReport(
  orderId: string,
  trackingToken: string,
): Promise<PDFDownloadResponse> {
  try {
    // Use HTTP endpoint for PDF download URL
    const response = await firebaseFunctionsApi.post<PDFDownloadResponse>(
      "/getCustomerReportDownloadUrlHttp",
      {
        orderId,
        trackingToken,
      },
    )

    if (!response.ok) {
      const errorData = response.data as any

      switch (response.status) {
        case 401:
          throw new Error("Invalid tracking token")
        case 412:
          throw new Error("PDF not available yet")
        case 404:
          throw new Error("PDF file not found")
        default:
          throw new Error(`Download failed: ${errorData?.error || response.problem}`)
      }
    }

    return response.data as PDFDownloadResponse
  } catch (error: any) {
    if (!isTransientNetworkError(error)) {
      Sentry.captureException(error, {
        tags: { flow: "pdf", operation: "download" },
      })
    }
    // Re-throw if it's already a processed error
    if (error.message) {
      throw error
    }
    // Otherwise, throw a generic error
    throw new Error(`Download failed: ${error}`)
  }
}

export async function authenticateCustomerPusher(
  request: PusherAuthRequest,
): Promise<PusherAuthResponse> {
  try {
    // Use HTTP endpoint for Pusher authentication
    // Note: This endpoint might need to be created in the backend if it doesn't exist
    const response = await firebaseFunctionsApi.post<PusherAuthResponse>(
      "/authenticateCustomerPusherHttp",
      request,
    )

    if (!response.ok) {
      const errorData = response.data as any

      switch (response.status) {
        case 401:
          throw new Error("Invalid tracking token")
        case 403:
          throw new Error("Permission denied")
        default:
          throw new Error(`Authentication failed: ${errorData?.error || response.problem}`)
      }
    }

    return response.data as PusherAuthResponse
  } catch (error: any) {
    // Re-throw if it's already a processed error
    if (error.message) {
      throw error
    }
    // Otherwise, throw a generic error
    throw new Error(`Pusher authentication failed: ${error}`)
  }
}

export async function getAllLocations(): Promise<HubLocation[]> {
  try {
    // Call the public locations HTTP function
    const response = await firebaseFunctionsApi.post<LocationsResponse>(
      "/getAllPublicLocationsHttp",
      {},
    )

    if (!response.ok) {
      const errorData = response.data as any
      throw new Error(errorData?.error || `Failed to fetch locations: ${response.problem}`)
    }

    const data = response.data as LocationsResponse

    if (!data.success) {
      throw new Error(data.error || "Failed to fetch locations")
    }

    return data.locations
  } catch (error: any) {
    // Re-throw if it's already a processed error
    if (error.message) {
      throw error
    }
    // Otherwise, throw a generic error
    throw new Error(`Failed to fetch locations: ${error}`)
  }
}

/**
 * FleetHub Types
 */
export interface FleetOrder {
  id: string
  hubspotTicketId: string
  customerNumber: string
  customerName: string
  numberPlate: string
  companyName?: string
  orderType?: string
  serviceType: "pickup_delivery" | "driveby"
  zustandigePrufstelle?: string
  // Location Data
  hubLocation?: {
    id: string
    name: string
    address: string
    latitude: number
    longitude: number
  }
  customerLocation?: {
    address: string
    latitude: number
    longitude: number
  }
  status: string
  orderPhase?: string
  scheduledPickupTime?: string
  scheduledReturnTime?: string
  spatesteAbholung?: string
  spatesteRuckgabe?: string
  inspectionPassed?: boolean | null
  inspectionResult?: string
  berichtPdfStatus?: "pending" | "available" | "error"
  berichtPdfId?: string
  trackingToken?: string
  ticketRescheduling?: string
  ticketCancellation?: string
  createdAt: Date
  updatedAt: Date
}

/**
 * Date range filter for filtering orders by scheduled pickup time
 */
export interface DateRangeFilter {
  type: "all" | "today" | "thisWeek" | "thisMonth" | "custom"
  startDate?: string // ISO string for custom range
  endDate?: string // ISO string for custom range
}

export interface GetFleetOrdersRequest {
  page?: number
  pageSize?: number
  sortBy?: "date" | "status"
  sortDirection?: "asc" | "desc"
  dateRange?: DateRangeFilter // Optional date range filtering
  numberPlate?: string // Optional number plate prefix search
}

export interface GetFleetOrdersResponse {
  orders: FleetOrder[]
  totalCount: number
  page: number
  pageSize: number
  hasMore: boolean
  changedInitialPassword?: boolean
}

/**
 * Convert Firestore Timestamp to JavaScript Date
 *
 * Handles various timestamp formats from Firebase:
 * - Firestore Timestamp objects with _seconds
 * - ISO date strings
 * - Existing Date objects
 *
 * @param timestamp - Firestore timestamp or date value
 * @returns JavaScript Date object
 */
function convertFirestoreTimestamp(timestamp: any): Date {
  if (!timestamp) return new Date()
  if (timestamp instanceof Date) return timestamp
  if (timestamp._seconds) {
    return new Date(timestamp._seconds * 1000)
  }
  if (typeof timestamp === "string") return new Date(timestamp)
  return new Date()
}

/**
 * Transform backend order to frontend FleetOrder format
 *
 * Converts Firestore Timestamp objects to JavaScript Dates
 * to ensure proper rendering and state management in React.
 *
 * @param backendOrder - Raw order data from Firebase Functions
 * @returns Transformed order with Date objects
 */
function transformFleetOrder(backendOrder: any): FleetOrder {
  return {
    ...backendOrder,
    createdAt: convertFirestoreTimestamp(backendOrder.createdAt),
    updatedAt: convertFirestoreTimestamp(backendOrder.updatedAt),
  }
}

/**
 * Get fleet orders for authenticated B2B customer
 *
 * Uses Firebase callable function with automatic authentication.
 * User must be logged in via Firebase Auth before calling this function.
 *
 * @param params - Pagination and sorting parameters
 * @returns Paginated list of fleet orders
 */
export async function getFleetOrders(
  params: GetFleetOrdersRequest,
): Promise<GetFleetOrdersResponse> {
  try {
    const data = await callableWithAuthRetry<GetFleetOrdersRequest, GetFleetOrdersResponse>(
      "getFleetOrders",
      params,
      "read",
    )

    // Transform orders with proper Date objects
    const transformedOrders = data.orders.map(transformFleetOrder)

    return {
      ...data,
      orders: transformedOrders,
    }
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw rewrapCallableError("Bitte melde dich an, um fortzufahren.", error)
      case "permission-denied":
        throw rewrapCallableError("Du hast keine Berechtigung, diese Aufträge anzuzeigen.", error)
      case "not-found":
        throw rewrapCallableError("Keine Aufträge gefunden.", error)
      case "invalid-argument":
        throw rewrapCallableError("Ungültige Anfrageparameter.", error)
      default:
        throw rewrapCallableError(
          errorMessage || "Fehler beim Laden der Aufträge. Bitte versuchen Sie es erneut.",
          error,
        )
    }
  }
}

/**
 * Confirm that the B2B customer has changed their initial password.
 * Sets changedInitialPassword to true in the user's Firestore document.
 */
export async function confirmPasswordChanged(): Promise<void> {
  await callableWithAuthRetry<void, void>(
    "confirmPasswordChanged",
    undefined as unknown as void,
    "mutation",
  )
}

/**
 * Get map data for FleetHub dashboard
 *
 * Uses Firebase callable function with automatic authentication.
 * Returns the authenticated customer's location and all hub locations.
 * User must be logged in via Firebase Auth before calling this function.
 *
 * @returns Map data including customer location and hub locations
 */
export async function getFleetHubMapData(): Promise<FleetHubMapData> {
  try {
    return await callableWithAuthRetry<void, FleetHubMapData>(
      "getFleetHubMapData",
      undefined as unknown as void,
      "read",
    )
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw rewrapCallableError("Bitte melde dich an, um fortzufahren.", error)
      case "permission-denied":
        throw rewrapCallableError("Du hast keine Berechtigung, diese Daten anzuzeigen.", error)
      case "not-found":
        throw rewrapCallableError("Kundendaten nicht gefunden.", error)
      case "failed-precondition":
        throw rewrapCallableError("Standortdaten nicht verfügbar.", error)
      default:
        throw rewrapCallableError(
          errorMessage || "Fehler beim Laden der Kartendaten. Bitte versuchen Sie es erneut.",
          error,
        )
    }
  }
}

/**
 * Request a tracking link resend email for a B2C customer.
 * Always returns success to prevent email enumeration.
 */
export async function resendTrackingLink(email: string): Promise<{ success: boolean }> {
  try {
    const response = await firebaseFunctionsApi.post<{ success: boolean }>(
      "/resendTrackingLinkHttp",
      { email },
    )

    if (!response.ok) {
      return { success: true }
    }

    return response.data ?? { success: true }
  } catch (error: any) {
    if (!isTransientNetworkError(error)) {
      Sentry.captureException(error, {
        tags: { flow: "tracking", operation: "resend-tracking-link" },
      })
    }
    return { success: true }
  }
}

/**
 * Opening Hours Types
 */
export interface DaySchedule {
  isOpen: boolean
  open?: string
  close?: string
}

export interface SpecialDate {
  isOpen: boolean
  open?: string
  close?: string
  reason?: string
}

export interface WeeklySchedule {
  monday: DaySchedule
  tuesday: DaySchedule
  wednesday: DaySchedule
  thursday: DaySchedule
  friday: DaySchedule
  saturday: DaySchedule
  sunday: DaySchedule
}

export interface OpeningHoursResponse {
  schedule: WeeklySchedule
  timezone: string
  specialDates?: {
    [date: string]: SpecialDate
  }
  resourceConfig?: {
    numEngineers: number
    numAssistants: number
  }
  /**
   * Service types this location does not offer (ADR-0051). Omitted by the
   * backend when the location has no restriction — and by any backend that
   * predates the field — so an absent key means both service types are offered.
   * Not to be confused with `disabledServiceIds` (Prüfarten, the *what* axis).
   */
  disabledServiceTypes?: FleetServiceType[]
}

/**
 * Inspection type IDs accepted by createFleetHubOrder. Mirrors the HDP B2C
 * widget catalog. When omitted, backend falls back to the legacy `nachkontrolle`
 * boolean derivation (HU/AU or Nachkontrolle) — fully backwards compatible.
 */
export type InspectionType =
  | "hu_au"
  | "hu"
  | "unfallgutachten"
  | "vollgutachten_21"
  | "eintragung_19"
  | "nachkontrolle"
  | "fruehjahrscheck"

/**
 * FleetHub Order Creation Types
 */
export interface CreateFleetHubOrderRequest {
  abholdatum: string // Pickup date in YYYY-MM-DD format
  spatesteAbholung: string // Pickup time in HH:MM format
  nachkontrolle: boolean // Follow-up inspection flag (legacy; superseded by inspectionType when provided)
  walkIn?: boolean // Self-drive to inspection center (no pickup/delivery)
  childCompanyId?: string // Optional child company ID for parent-child company support
  numberPlate?: string // Optional pre-formatted number plate (e.g. "B-AB 1234")
  inspectionType?: InspectionType // Optional explicit inspection product
  /** Free-form customer message (PLA-149/151). Omitted when empty after trim. */
  customerNotes?: string
}

export interface CreateFleetHubOrderResponse {
  success: boolean
  ticketId: string
  message: string
}

/**
 * FleetHub Order Rescheduling Types
 */
export interface RescheduleFleetHubOrderRequest {
  orderId: string // HubSpot ticket ID
  abholdatum: string // New pickup date in YYYY-MM-DD format
  spatesteAbholung: string // New pickup time in HH:MM format
}

export interface RescheduleFleetHubOrderResponse {
  success: boolean
  message: string
}

/**
 * FleetHub Order Cancellation Types
 */
export interface CancelFleetHubOrderRequest {
  orderId: string // HubSpot ticket ID
}

export interface CancelFleetHubOrderResponse {
  success: boolean
  message: string
}

/**
 * Create a new FleetHub order
 *
 * Uses Firebase callable function with automatic authentication.
 * Creates a ticket in HubSpot which will automatically sync back to Firestore.
 * User must be logged in via Firebase Auth before calling this function.
 *
 * @param data - Order creation data (pickup date, time, follow-up flag)
 * @returns Response with success status and ticket ID
 */
export async function createFleetHubOrder(
  data: CreateFleetHubOrderRequest,
): Promise<CreateFleetHubOrderResponse> {
  try {
    return await callableWithAuthRetry<CreateFleetHubOrderRequest, CreateFleetHubOrderResponse>(
      "createFleetHubOrder",
      data,
      "mutation",
    )
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    // The Firebase JS SDK reports namespaced codes (`functions/…`); the bare
    // variants are kept for callers/tests that hand us an already-normalised
    // error. `rewrapCallableError` preserves `.code` so the store can tier the
    // failure and Sentry can downgrade validation throws to WARN (PLA-534).
    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw rewrapCallableError("Bitte melde dich an, um fortzufahren.", error)
      case "functions/permission-denied":
      case "permission-denied":
        throw rewrapCallableError("Sie haben keine Berechtigung, Aufträge zu erstellen.", error)
      case "functions/invalid-argument":
      case "invalid-argument":
        throw rewrapCallableError(errorMessage || "Ungültige Eingabedaten.", error)
      // Server-side booking gates, e.g. Hol- und Bring-Service disabled at the
      // hub resolved from the company's expansionRegion (ADR-0051). The backend
      // message is already customer-ready German — surface it verbatim.
      case "functions/failed-precondition":
      case "failed-precondition":
        throw rewrapCallableError(errorMessage || "Auftrag kann nicht erstellt werden.", error)
      case "functions/not-found":
      case "not-found":
        throw rewrapCallableError("Kundendaten nicht gefunden.", error)
      default:
        throw rewrapCallableError(
          errorMessage || "Fehler beim Erstellen des Auftrags. Bitte versuchen Sie es erneut.",
          error,
        )
    }
  }
}

/**
 * Reschedule an existing FleetHub order
 *
 * Uses Firebase callable function with automatic authentication.
 * Updates the pickup date/time in HubSpot and moves ticket back to early stage.
 * User must be logged in via Firebase Auth and must own the order.
 *
 * @param data - Reschedule data (order ID, new pickup date, time)
 * @returns Response with success status
 */
export async function rescheduleFleetHubOrder(
  data: RescheduleFleetHubOrderRequest,
): Promise<RescheduleFleetHubOrderResponse> {
  try {
    return await callableWithAuthRetry<
      RescheduleFleetHubOrderRequest,
      RescheduleFleetHubOrderResponse
    >("rescheduleFleetHubOrder", data, "mutation")
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw new Error("Bitte melde dich an, um fortzufahren.")
      case "permission-denied":
        throw new Error("Sie haben keine Berechtigung, diesen Auftrag zu ändern.")
      case "invalid-argument":
        throw new Error(errorMessage || "Ungültige Eingabedaten.")
      case "not-found":
        throw new Error("Auftrag nicht gefunden.")
      default:
        throw new Error(
          errorMessage || "Fehler beim Verschieben des Auftrags. Bitte versuchen Sie es erneut.",
        )
    }
  }
}

/**
 * Cancel an existing FleetHub order
 *
 * Uses Firebase callable function with automatic authentication.
 * Updates the order stage to "cancelled" in HubSpot.
 * User must be logged in via Firebase Auth and must own the order.
 *
 * @param data - Cancel data (order ID)
 * @returns Response with success status
 */
export async function cancelFleetHubOrder(
  data: CancelFleetHubOrderRequest,
): Promise<CancelFleetHubOrderResponse> {
  try {
    return await callableWithAuthRetry<CancelFleetHubOrderRequest, CancelFleetHubOrderResponse>(
      "cancelFleetHubOrder",
      data,
      "mutation",
    )
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw new Error("Bitte melde dich an, um fortzufahren.")
      case "permission-denied":
        throw new Error("Sie haben keine Berechtigung, diesen Auftrag zu stornieren.")
      case "invalid-argument":
        throw new Error(errorMessage || "Ungültige Eingabedaten.")
      case "not-found":
        throw new Error("Auftrag nicht gefunden.")
      default:
        throw new Error(
          errorMessage || "Fehler beim Stornieren des Auftrags. Bitte versuchen Sie es erneut.",
        )
    }
  }
}

/**
 * Get the customer's expansion region
 *
 * Uses Firebase callable function with automatic authentication.
 * Returns the expansion region (location name) for the authenticated customer.
 * User must be logged in via Firebase Auth before calling this function.
 *
 * @returns Expansion region name (e.g., "Berlin", "Nürnberg Süd") or null
 */
export async function getCustomerExpansionRegion(): Promise<string | null> {
  try {
    const data = await callableWithAuthRetry<void, { expansionRegion: string | null }>(
      "getCustomerExpansionRegion",
      undefined as unknown as void,
      "read",
    )
    return data.expansionRegion
  } catch {
    return null
  }
}

/**
 * Get customer companies (parent + child companies)
 *
 * Uses Firebase callable function with automatic authentication.
 * Returns the parent company and all child companies for the authenticated customer.
 * User must be logged in via Firebase Auth before calling this function.
 *
 * @returns Parent and child company options with IDs, names, and expansion regions
 */
export async function getCustomerCompanies(): Promise<CustomerCompaniesResponse> {
  try {
    return await callableWithAuthRetry<void, CustomerCompaniesResponse>(
      "getCustomerCompanies",
      undefined as unknown as void,
      "read",
    )
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    // Expected validation outcomes ("Kein gültiges B2B-Kundenkonto" and token
    // expiry) — these are by-design backend throws (ADR-0020 WARN tier), not
    // actionable client bugs. Suppress from Sentry to avoid noise.
    //
    // PLA-534: this list used to be hand-written with *bare* codes and omitted
    // permission-denied, so it never once matched the throw its comment names —
    // the Firebase JS SDK namespaces callable codes as `functions/…`. Bind to the
    // canonical set instead of restating it, so the two can no longer drift.
    const isExpectedValidation = EXPECTED_FUNCTION_ERROR_CODES.has(errorCode)
    if (!isExpectedValidation) {
      Sentry.captureException(error, {
        tags: { flow: "fleethub", operation: "get-customer-companies" },
      })
    }

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw rewrapCallableError("Bitte melde dich an, um fortzufahren.", error)
      case "permission-denied":
        throw rewrapCallableError("Du hast keine Berechtigung, diese Daten anzuzeigen.", error)
      case "not-found":
        throw rewrapCallableError("Kundendaten nicht gefunden.", error)
      default:
        throw rewrapCallableError(
          errorMessage || "Fehler beim Laden der Firmendaten. Bitte versuchen Sie es erneut.",
          error,
        )
    }
  }
}

/**
 * Get opening hours for a specific location
 *
 * Uses Firebase callable function to fetch location-specific opening hours.
 * Returns weekly schedule, timezone, and special dates (holidays).
 *
 * @param location - Location name (e.g., "Berlin", "Nürnberg Süd")
 * @returns Opening hours schedule with timezone and special dates
 */
export async function getLocationOpeningHours(location: string): Promise<OpeningHoursResponse> {
  const startTime = Date.now()
  console.log(`[functions] 🔄 Fetching opening hours for location: "${location}"...`)

  try {
    const result = await callableWithAuthRetry<{ location: string }, OpeningHoursResponse>(
      "getLocationOpeningHours",
      { location },
      "read",
    )
    const elapsed = Date.now() - startTime

    console.log(`[functions] ✅ Opening hours fetched in ${elapsed}ms`)
    console.log("[functions] Schedule data:", JSON.stringify(result.schedule, null, 2))
    console.log("[functions] Timezone:", result.timezone)
    console.log(
      "[functions] Special dates:",
      result.specialDates ? Object.keys(result.specialDates).length + " dates" : "none",
    )

    return result
  } catch (error: any) {
    const elapsed = Date.now() - startTime
    console.error(`[functions] ❌ Error fetching opening hours (after ${elapsed}ms):`, error)
    console.error("[functions] Error code:", error.code)
    console.error("[functions] Error message:", error.message)
    console.error("[functions] Error details:", error.details)
    console.warn("[functions] ⚠️  Returning default fallback opening hours")

    // Return default opening hours as fallback
    return {
      schedule: {
        monday: { isOpen: true, open: "09:00", close: "17:00" },
        tuesday: { isOpen: true, open: "09:00", close: "17:00" },
        wednesday: { isOpen: true, open: "09:00", close: "17:00" },
        thursday: { isOpen: true, open: "09:00", close: "17:00" },
        friday: { isOpen: true, open: "09:00", close: "17:00" },
        saturday: { isOpen: false },
        sunday: { isOpen: false },
      },
      timezone: "Europe/Berlin",
      specialDates: {
        // German public holidays 2025
        "2025-01-01": { isOpen: false, reason: "Neujahr" },
        "2025-04-18": { isOpen: false, reason: "Karfreitag" },
        "2025-04-21": { isOpen: false, reason: "Ostermontag" },
        "2025-05-01": { isOpen: false, reason: "Tag der Arbeit" },
        "2025-05-29": { isOpen: false, reason: "Christi Himmelfahrt" },
        "2025-06-09": { isOpen: false, reason: "Pfingstmontag" },
        "2025-10-03": { isOpen: false, reason: "Tag der Deutschen Einheit" },
        "2025-12-25": { isOpen: false, reason: "1. Weihnachtsfeiertag" },
        "2025-12-26": { isOpen: false, reason: "2. Weihnachtsfeiertag" },
      },
    }
  }
}

/**
 * Optimal Booking Times Types
 */
export interface OptimalBookingTimesResponse {
  recommendedTime: string | null
  goodTimes: string[] // Top 50% of feasible slots (best availability)
  mediumTimes: string[] // Bottom 50% of feasible slots (acceptable availability)
  busyTimes: string[] // Infeasible slots (cannot be booked due to resource conflicts)
  allSlots: Array<{
    time: string
    score: number
    isFeasible: boolean
    scoreBreakdown?: {
      earliness: number
      gapEfficiency: number
      loadBalance: number
      buffer: number
    }
  }>
  capacity: {
    engineerUtilization: number
    assistantUtilization: number
  }
  metadata: {
    totalSlots: number
    feasibleSlots: number
    date: string
    location: string
    resourceConfig: {
      numEngineers: number
      numAssistants: number
    }
  }
}

/**
 * Get optimal booking times based on resource availability
 *
 * Uses Firebase callable function to fetch intelligent time slot recommendations
 * based on current resource utilization and booking patterns.
 *
 * @param zustandigePrufstelle - Inspection center location (e.g., "Berlin", "Nürnberg")
 * @param date - Booking date in YYYY-MM-DD format
 * @param currentTime - Current time in HH:MM format
 * @param serviceType - Service type: 'pickup_delivery' or 'driveby'
 * @param numInspectionEngineers - Optional: Number of inspection engineers (defaults to backend config)
 * @param numInspectionAssistants - Optional: Number of inspection assistants (defaults to backend config)
 * @returns Optimal booking times with recommendations, capacity info, and metadata
 */
export async function getOptimalBookingTimes(
  zustandigePrufstelle: string,
  date: string,
  currentTime: string,
  serviceType: "pickup_delivery" | "driveby",
  numInspectionEngineers?: number,
  numInspectionAssistants?: number,
): Promise<OptimalBookingTimesResponse> {
  const startTime = Date.now()
  console.log(`[functions] 🔄 Fetching optimal booking times for "${zustandigePrufstelle}"...`)

  try {
    const result = await callableWithAuthRetry<
      {
        zustandigePrufstelle: string
        date: string
        currentTime: string
        serviceType: "pickup_delivery" | "driveby"
        numInspectionEngineers?: number
        numInspectionAssistants?: number
      },
      OptimalBookingTimesResponse
    >(
      "getOptimalBookingTimesFn",
      {
        zustandigePrufstelle,
        date,
        currentTime,
        serviceType,
        ...(typeof numInspectionEngineers === "number" ? { numInspectionEngineers } : {}),
        ...(typeof numInspectionAssistants === "number" ? { numInspectionAssistants } : {}),
      },
      "read",
    )

    const elapsed = Date.now() - startTime
    console.log(`[functions] ✅ Optimal booking times fetched in ${elapsed}ms`)
    console.log("[functions] Recommended time:", result.recommendedTime)
    console.log("[functions] Good times count:", result.goodTimes.length)
    console.log("[functions] Busy times count:", result.busyTimes.length)
    console.log("[functions] Engineer utilization:", result.capacity.engineerUtilization + "%")
    console.log("[functions] Assistant utilization:", result.capacity.assistantUtilization + "%")

    return result
  } catch (error: any) {
    const elapsed = Date.now() - startTime
    console.error(
      `[functions] ❌ Error fetching optimal booking times (after ${elapsed}ms):`,
      error,
    )
    console.error("[functions] Error code:", error.code)
    console.error("[functions] Error message:", error.message)
    console.error("[functions] Error details:", error.details)

    // Handle Firebase Functions errors
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "unauthenticated":
        throw new Error("Bitte melde dich an, um fortzufahren.")
      case "permission-denied":
        throw new Error("Sie haben keine Berechtigung, diese Funktion zu nutzen.")
      case "invalid-argument":
        throw new Error(errorMessage || "Ungültige Eingabedaten.")
      case "not-found":
        throw new Error("Standort nicht gefunden.")
      default:
        throw new Error(
          errorMessage || "Fehler beim Laden der optimalen Zeiten. Bitte versuchen Sie es erneut.",
        )
    }
  }
}

/**
 * Get optimal booking times V2 — Digital Twin
 *
 * Uses the assignment engine's plan document for real PA/PI capacity.
 * Same interface as V1 for frontend compatibility.
 */
export async function getOptimalBookingTimesV2(
  zustandigePrufstelle: string,
  date: string,
  currentTime: string,
  serviceType: "pickup_delivery" | "driveby",
  estimatedLegMinutes?: number,
): Promise<OptimalBookingTimesResponse> {
  const startTime = Date.now()
  console.log(`[functions] Fetching optimal booking times V2 for "${zustandigePrufstelle}"...`)

  try {
    const result = await callableWithAuthRetry<
      {
        zustandigePrufstelle: string
        date: string
        currentTime: string
        serviceType: "pickup_delivery" | "driveby"
        estimatedLegMinutes?: number
      },
      OptimalBookingTimesResponse
    >(
      "getOptimalBookingTimesV2Fn",
      {
        zustandigePrufstelle,
        date,
        currentTime,
        serviceType,
        ...(typeof estimatedLegMinutes === "number" && estimatedLegMinutes >= 5
          ? { estimatedLegMinutes }
          : {}),
      },
      "read",
    )

    const elapsed = Date.now() - startTime
    console.log(`[functions] Optimal booking times V2 fetched in ${elapsed}ms`)
    console.log("[functions] Recommended time:", result.recommendedTime)
    console.log("[functions] Feasible slots:", result.allSlots.length)

    return result
  } catch (error: any) {
    const elapsed = Date.now() - startTime
    console.error(
      `[functions] Error fetching optimal booking times V2 (after ${elapsed}ms):`,
      error,
    )

    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "unauthenticated":
        throw new Error("Bitte melde dich an, um fortzufahren.")
      case "permission-denied":
        throw new Error("Sie haben keine Berechtigung, diese Funktion zu nutzen.")
      case "invalid-argument":
        throw new Error(errorMessage || "Ungültige Eingabedaten.")
      case "not-found":
        throw new Error("Standort nicht gefunden.")
      default:
        throw new Error(
          errorMessage || "Fehler beim Laden der optimalen Zeiten. Bitte versuchen Sie es erneut.",
        )
    }
  }
}

// ─── Booking Times (Active) ──────────────────────────────────────────────────

/**
 * Active booking times function — always uses the V2 entry point.
 *
 * The V2 callable (`getOptimalBookingTimesV2Fn`) allows unauthenticated B2C access
 * and internally delegates to V1 or V2 computation based on the backend's BOOKING_MODE.
 * The V1 callable (`getOptimalBookingTimesFn`) requires B2B auth and blocks B2C users.
 */
export async function getOptimalBookingTimesActive(
  zustandigePrufstelle: string,
  date: string,
  currentTime: string,
  serviceType: "pickup_delivery" | "driveby",
  estimatedLegMinutes?: number,
): Promise<OptimalBookingTimesResponse> {
  return getOptimalBookingTimesV2(
    zustandigePrufstelle,
    date,
    currentTime,
    serviceType,
    estimatedLegMinutes,
  )
}

// ─── Billing Documents ───────────────────────────────────────────────────────

export interface BillingDocument {
  fileId: string
  filename: string
  date: string // ISO date string (YYYY-MM-DD)
  downloadUrl: string // HubSpot signed URL (time-limited)
  size?: number // File size in bytes
}

export interface InspectionReport {
  orderId: string
  fileId: string
  filename: string
  date: string // YYYY-MM-DD
  numberPlate: string
  size?: number
  // No downloadUrl — fetched on demand via getReportDownloadUrl
}

export interface ReportDownloadUrlResponse {
  url: string
  filename: string
  expiresAt: number
  size?: number
}

export interface GetCompanyBillingDocumentsResponse {
  documents: BillingDocument[]
  reports: InspectionReport[]
  totalCount: number
  totalReports: number
}

/**
 * Fetch billing documents (PDF invoices/receipts) for the authenticated B2B customer.
 * Returns documents from parent company + all child companies, sorted by date (newest first).
 */
export async function getCompanyBillingDocuments(): Promise<GetCompanyBillingDocumentsResponse> {
  try {
    return await callableWithAuthRetry<void, GetCompanyBillingDocumentsResponse>(
      "getCompanyBillingDocuments",
      undefined as unknown as void,
      "read",
    )
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw new Error("Bitte melde dich an, um fortzufahren.")
      case "permission-denied":
        throw new Error("Du hast keine Berechtigung, diese Dokumente anzuzeigen.")
      case "failed-precondition":
        throw new Error(errorMessage || "Rechnungsdaten nicht verfügbar.")
      default:
        throw new Error(
          errorMessage || "Fehler beim Laden der Rechnungen. Bitte versuchen Sie es erneut.",
        )
    }
  }
}

/**
 * Get a signed download URL for a single inspection report.
 * Called on demand when the user clicks download on a specific report.
 */
export async function getReportDownloadUrl(orderId: string): Promise<ReportDownloadUrlResponse> {
  try {
    return await callableWithAuthRetry<{ orderId: string }, ReportDownloadUrlResponse>(
      "getReportDownloadUrl",
      { orderId },
      "read",
    )
  } catch (error: any) {
    const errorCode = error.code
    const errorMessage = error.message

    switch (errorCode) {
      case "functions/unauthenticated":
      case "unauthenticated":
        throw new Error("Bitte melde dich an, um fortzufahren.")
      case "permission-denied":
        throw new Error("Kein Zugriff auf diesen Prüfbericht.")
      case "not-found":
        throw new Error("Auftrag nicht gefunden.")
      case "failed-precondition":
        throw new Error("Prüfbericht nicht verfügbar.")
      default:
        throw new Error(
          errorMessage || "Fehler beim Laden des Prüfberichts. Bitte versuchen Sie es erneut.",
        )
    }
  }
}

// ─── B2C Tracking Actions (token-based, no Firebase Auth) ───────────

export interface RescheduleB2cRequest {
  orderId: string
  trackingToken: string
  abholdatum: string // YYYY-MM-DD
  spatesteAbholung: string // HH:MM
}

export interface RescheduleB2cResponse {
  success: boolean
  message: string
}

export interface CancelB2cResponse {
  success: boolean
  message: string
}

export interface B2cOpeningHoursResponse {
  success: boolean
  location: string
  openingHours: {
    timezone: string
    schedule: {
      monday: { isOpen: boolean; open?: string; close?: string }
      tuesday: { isOpen: boolean; open?: string; close?: string }
      wednesday: { isOpen: boolean; open?: string; close?: string }
      thursday: { isOpen: boolean; open?: string; close?: string }
      friday: { isOpen: boolean; open?: string; close?: string }
      saturday: { isOpen: boolean; open?: string; close?: string }
      sunday: { isOpen: boolean; open?: string; close?: string }
    }
    specialDates?: Record<
      string,
      { isOpen: boolean; open?: string; close?: string; reason?: string }
    >
  }
}

/**
 * Reschedule a B2C order using tracking token authentication
 */
export async function rescheduleB2cOrder(
  data: RescheduleB2cRequest,
): Promise<RescheduleB2cResponse> {
  try {
    return await callableWithAuthRetry<RescheduleB2cRequest, RescheduleB2cResponse>(
      "rescheduleB2cOrder",
      data,
      "mutation",
    )
  } catch (error: any) {
    const errorMessage = error.message
    // The code travels with the Error so the picker can tell slot-taken from transient (PLA-881).
    const fail = (message: string) => Object.assign(new Error(message), { code: error.code })
    switch (error.code) {
      case "unauthenticated":
        throw fail("Ungültiger oder abgelaufener Tracking-Link.")
      case "invalid-argument":
        throw fail(errorMessage || "Ungültige Eingabedaten.")
      case "not-found":
        throw fail("Auftrag nicht gefunden.")
      default:
        throw fail(errorMessage || "Fehler beim Verschieben. Bitte versuchen Sie es erneut.")
    }
  }
}

/**
 * Cancel a B2C order using tracking token authentication
 */
export async function cancelB2cOrder(
  orderId: string,
  trackingToken: string,
): Promise<CancelB2cResponse> {
  try {
    return await callableWithAuthRetry<
      { orderId: string; trackingToken: string },
      CancelB2cResponse
    >("cancelB2cOrder", { orderId, trackingToken }, "mutation")
  } catch (error: any) {
    // Capture the original callable error (code intact) before the rewrap below drops it,
    // so beforeSend's downgradeExpectedThrow can tier validation codes to WARN (PLA-903).
    // Expired/reused tracking links (unauthenticated) are an expected user condition, not captured.
    const staleLink =
      error?.code === "functions/unauthenticated" || error?.code === "unauthenticated"
    if (!isTransientNetworkError(error) && !staleLink) {
      Sentry.captureException(error, {
        tags: { flow: "reschedule", operation: "cancel-b2c" },
      })
    }
    const errorMessage = error.message
    switch (error.code) {
      case "unauthenticated":
        throw new Error("Ungültiger oder abgelaufener Tracking-Link.")
      case "not-found":
        throw new Error("Auftrag nicht gefunden.")
      default:
        throw new Error(errorMessage || "Fehler beim Stornieren. Bitte versuchen Sie es erneut.")
    }
  }
}

/**
 * Get opening hours for B2C reschedule (token-based, no Firebase Auth)
 */
export async function getB2cOpeningHours(
  location: string,
  trackingToken: string,
): Promise<B2cOpeningHoursResponse> {
  try {
    return await callableWithAuthRetry<
      { location: string; trackingToken: string },
      B2cOpeningHoursResponse
    >("getB2cOpeningHours", { location, trackingToken }, "read")
  } catch (error: any) {
    throw new Error(error.message || "Fehler beim Laden der Öffnungszeiten.")
  }
}

// ─── Survey (token-based, no Firebase Auth) ──────────────────

export type SurveyType = "vehicle_type" | "vehicle_age" | "next_inspection_date" | "referral"

export interface SubmitReferralSurveyRequest {
  trackingToken: string
  answer: string
  surveyType?: SurveyType
}

export interface SubmitReferralSurveyResponse {
  success: boolean
}

/**
 * Submit referral survey answer using tracking token authentication
 */
export async function submitReferralSurvey(
  data: SubmitReferralSurveyRequest,
): Promise<SubmitReferralSurveyResponse> {
  try {
    const response = await surveyApi.post<SubmitReferralSurveyResponse>("/", data)

    if (!response.ok) {
      const errorData = response.data as any
      // Preserve the apisauce problem code in the error message so that
      // isTransientNetworkError() can detect NETWORK_ERROR / TIMEOUT_ERROR
      // in the catch block below (otherwise the message is the generic
      // "Survey submission failed" fallback and the transient check misses it).
      throw new Error(errorData?.error || `Survey submission failed: ${response.problem}`)
    }

    return response.data as SubmitReferralSurveyResponse
  } catch (error: any) {
    const msg: string = error?.message ?? ""
    // "Order not found" during the ~40s HubSpot→Firestore sync window is
    // expected (same window as validateAndFetchOrder). Suppress from Sentry
    // and leave a breadcrumb so the event is still traceable in session replays.
    // Match the backend body exactly — a broad includes("not found") would also
    // swallow unrelated 404s (e.g. "survey endpoint not found") and kill their
    // observability. The throw above sets msg to errorData.error verbatim.
    const isSyncWindowMiss = msg === "Order not found"
    if (!isTransientNetworkError(error) && !isSyncWindowMiss) {
      Sentry.captureException(error, {
        tags: { flow: "survey", operation: `submit-${data.surveyType ?? "referral"}` },
      })
    } else if (isSyncWindowMiss) {
      Sentry.addBreadcrumb({
        category: "survey",
        message: "Survey submission suppressed — order not yet synced (sync window)",
        level: "info",
        data: { surveyType: data.surveyType ?? "referral" },
      })
    }
    if (error.message) throw error
    throw new Error(`Survey submission failed: ${error}`)
  }
}

// Export for backward compatibility (no longer used but kept for reference)
export const firebaseFunctions = {
  validateTrackingAccess: validateAndFetchOrder,
  authenticateCustomerPusher,
  getCustomerReportDownloadUrl: downloadPDFReport,
  getFleetOrders,
  getFleetHubMapData,
}
